/**
 * How the moderator's screen draws the match list's teams, pure so its rules
 * are held by tests rather than by a screen: the team an open cohort card
 * draws as the list is read again under it, and a profit's tone and text.
 */

import type { Game, GameWorld, PlayerCohort } from '../../api/moderator/moderation/types';

/**
 * Where the list showed a player as their card opened: the game, the world
 * and the team, which a private chat's room is named by.
 */
export interface PlayerPlace {
  gameId: string;
  worldName: string;
  cohortId: string;
  cohortName: string;
}

/** A team a cohort card is open on: the team, and where it is. */
export interface CohortContext {
  cohort: PlayerCohort;
  /** The game's id (e.g. "ponder-flight-game"). */
  gameId: string;
  /** The world's name. */
  worldId: string;
  /** The full Nakama match id (e.g. "uuid.nakama1"). */
  matchId: string;
  /** The UUID part of the match id. */
  matchUUID: string;
  /** When the session started, e.g. "20260311T180000Z". */
  sessionTimestamp: string;
}

/** A team as the list has it, under its game and world. */
export function cohortContextOf(gameId: string, world: GameWorld, cohort: PlayerCohort): CohortContext {
  return {
    cohort,
    gameId,
    worldId: world.name,
    matchId: world.id,
    matchUUID: world.matchUUID ?? '',
    sessionTimestamp: world.sessionTimestamp ?? '',
  };
}

/**
 * The team an open cohort card draws, given the team it last drew: as the
 * list has it now; as it last drew it while its match is listed but could not
 * be read — busy at a poll, so listed with no teams, where the card had closed
 * and opened again by itself at the next poll; or null once its match, or its
 * team, is no longer listed. While nothing it draws has changed, the team it
 * last drew comes back itself, so the card holds it without being set again.
 */
export function cohortAsListed(games: readonly Game[], drawn: CohortContext): CohortContext | null {
  const world = games.find((game) => game.id === drawn.gameId)?.worlds.find((w) => w.id === drawn.matchId);
  if (!world) return null;
  if (world.detailsUnreadable) return drawn;
  const cohort = world.cohorts.find((c) => c.id === drawn.cohort.id);
  if (!cohort) return null;
  const listed = cohortContextOf(drawn.gameId, world, cohort);
  return sameDrawing(listed, drawn) ? drawn : listed;
}

/** Whether two of a team's contexts draw the same: the same team, read once, in the same world. */
function sameDrawing(a: CohortContext, b: CohortContext): boolean {
  return a.cohort === b.cohort && a.worldId === b.worldId && a.matchUUID === b.matchUUID && a.sessionTimestamp === b.sessionTimestamp;
}

/** A profit's tone: gained, lost, or nothing to say (no report yet, or even). */
export type ProfitTone = 'positive' | 'negative' | 'unavailable';

/** A profit's tone (ProfitTone), drawn by its class on the team's tile and on its card. */
export function profitTone(profit: number | null | undefined): ProfitTone {
  if (profit === undefined || profit === null || profit === 0) return 'unavailable';
  return profit > 0 ? 'positive' : 'negative';
}

/**
 * A profit as the screen writes it, in the game's millions: signed to two
 * places, or a dash with no report yet. The team's tile wrote it without its
 * unit, where its card wrote the same figure with one.
 */
export function profitText(profit: number | null | undefined): string {
  if (profit === undefined || profit === null) return '-';
  return `${profit >= 0 ? '+' : ''}${profit.toFixed(2)}M`;
}
